import "@near-wallet-selector/modal-ui/styles.css";
import { setupHotWallet } from "@hot-wallet/sdk/adapter/near";
import type {
  AccountState,
  WalletModuleFactory,
  WalletSelector,
  WalletSelectorState,
} from "@near-wallet-selector/core";
import { setupWalletSelector } from "@near-wallet-selector/core";
import type { WalletSelectorModal } from "@near-wallet-selector/modal-ui";
import { setupModal } from "@near-wallet-selector/modal-ui";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { distinctUntilChanged, map } from "rxjs";

import { useStore } from "@/hooks/store";
import { config } from "@/services/config";

declare global {
  interface Window {
    selector: WalletSelector;
    modal: WalletSelectorModal;
  }
}

interface WalletSelectorContextValue {
  selector: WalletSelector;
  modal: WalletSelectorModal;
  accountId: string | null;
}

const WalletSelectorContext = createContext<WalletSelectorContextValue | null>(null);

export const WalletSelectorContextProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [selector, setSelector] = useState<WalletSelector | null>(null);
  const [modal, setModal] = useState<WalletSelectorModal | null>(null);
  const [accountId, setAccountId] = useState<string>("");

  const updateAccounts = useStore((store) => store.updateAccounts);

  const initSelector = async () => {
    try {
      const selector = await setupWalletSelector({
        modules: [setupHotWallet() as WalletModuleFactory],
        network: "mainnet",
        fallbackRpcUrls: config.near.nodeUrls,
      });
      const modal = setupModal(selector, { contractId: "" });
      setSelector(selector);
      setModal(modal);
      return { selector, modal };
    } catch (e) {
      console.log("warn here", e);
    }
  };

  useEffect(() => {
    const init = async () => {
      const initializedSelector = await initSelector();
      if (!initializedSelector) return;

      const wallet = await initializedSelector.selector.wallet();
      const accounts = await wallet.getAccounts();

      initializedSelector.selector.on("signedIn", async (t) => {
        const wallet = await initializedSelector.selector.wallet();
        updateAccounts({ nearAccount: t.accounts[0].accountId });
        return { account: t.accounts[0].accountId, hot: wallet };
      });

      initializedSelector.selector.on("signedOut", async () => {
        throw Error("Not connected");
      });
      return { account: accounts[0].accountId, hot: wallet };
    };
    init();
  }, [updateAccounts]);

  const syncAccountState = (currentAccountId: string | null, newAccounts: Array<AccountState>) => {
    if (!newAccounts.length) {
      localStorage.removeItem("accountId");
      setAccountId("");

      return;
    }

    const validAccountId = currentAccountId && newAccounts.some((x) => x.accountId === currentAccountId);
    const newAccountId = validAccountId ? currentAccountId : newAccounts[0].accountId;
    updateAccounts({ nearAccount: newAccountId });

    localStorage.setItem("accountId", newAccountId);
    setAccountId(newAccountId);
  };

  useEffect(() => {
    if (!selector) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const subscription = (selector.store.observable as any)
      .pipe(
        map((state: WalletSelectorState) => state.accounts),
        distinctUntilChanged(),
      )
      .subscribe((nextAccounts: AccountState[]) => {
        syncAccountState(accountId, nextAccounts);
      });

    // eslint-disable-next-line consistent-return
    return () => subscription.unsubscribe();
  }, [selector, accountId]);

  const walletSelectorContextValue = useMemo<WalletSelectorContextValue>(
    () => ({
      selector: selector!,
      modal: modal!,
      accountId,
    }),
    [selector, modal, accountId],
  );

  return <WalletSelectorContext.Provider value={walletSelectorContextValue}>{children}</WalletSelectorContext.Provider>;
};

export function useWalletSelector() {
  const context = useContext(WalletSelectorContext);

  if (!context) {
    throw new Error("useWalletSelector must be used within a WalletSelectorContextProvider");
  }

  return context;
}
